import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Stack, TextField } from '@esgian/esgianui';
import { RigSelect } from '@components/Inputs';
import { BASELINE_TYPE, DATE_FORMAT, DATE_TIMEZONE_FORMAT } from '@constants/index';
import TargetDetails from '@components/Inputs/BaselineInput/BaselineModal/TargetDetails';
import moment from 'moment-timezone';
import DateRangeDropdown from '@components/DateRangeDropdown';
import tz_lookup from 'tz-lookup';

function BaselineDetails({ baseline, setBaseline, highlightErrors }) {
  const handleChange = useCallback(
    (val) => {
      setBaseline((prev) => ({ ...prev, ...val }));
    },
    [setBaseline]
  );

  const setSuggestedName = useCallback(() => {
    const { startDate, endDate, well, selectedRig } = baseline;
    let name = '';
    if (selectedRig) {
      name += selectedRig?.rigName;
    }
    if (well) {
      name += ` -  ${well.wellname}`;
    } else {
      if (startDate && endDate) {
        name += ` - ${moment(startDate, DATE_FORMAT).format(DATE_FORMAT)} - ${moment(
          endDate,
          DATE_FORMAT
        ).format(DATE_FORMAT)}`;
      }
    }
    handleChange({ baselineName: name });
  }, [baseline]);

  const minStartDate = useMemo(() => {
    const { selectedRig } = baseline;
    if (!selectedRig)
      return undefined;

    const timezone = tz_lookup(selectedRig.latitude, selectedRig.longitude);

    // lowerLimit is a yyyy-MM-dd date, so it is read as midnight in the rig's zone rather than parsed in
    // browser time and converted, which lands on the previous day for rigs behind the browser.
    const minDate = moment.tz(selectedRig.lowerLimit, DATE_FORMAT, timezone);
    return minDate.format(DATE_TIMEZONE_FORMAT);
  }, [baseline])

  return (
    <Box sx={{ p: 2 }}>
      {baseline?.baselineTypeId === BASELINE_TYPE.TARGET ? (
        <TargetDetails
          highlightErrors={highlightErrors}
          baseline={baseline}
          setBaseline={setBaseline}
        />
      ) : (
        <Stack spacing={3}>
          <RigSelect
            error={highlightErrors && !baseline.selectedRig?.rigId}
            helperText={highlightErrors && !baseline.selectedRig?.rigId ? 'Required' : ''}
            selectedRig={baseline.selectedRig ?? null}
            onChange={(rig) => handleChange({ selectedRig: rig })}
          />
          <DateRangeDropdown
            endDate={baseline?.endDate ?? null}
            startDate={baseline?.startDate ?? null}
            selectedDateName=""
            onSave={(val) => handleChange(val)}
            fullWidth
            helpertext={'Required'}
            error={highlightErrors && (!baseline.startDate || !baseline.endDate)}
            well={baseline.well}
            allowTimeZoneChange={false}
            currentRig={baseline.selectedRig || {}}
            preferredValue={baseline.name}
            minStartDate={minStartDate}
          />
          <TextField
            helperText={highlightErrors && !baseline.baselineName ? 'Please fill this field.' : ''}
            error={highlightErrors && !baseline.baselineName}
            InputLabelProps={{ shrink: true }}
            inputProps={{
              sx: { textOverflow: 'ellipsis', pt: '12px', pb: '12px' }
            }}
            InputProps={{
              sx: { paddingRight: 0 },
              endAdornment: (
                <Button
                  onClick={setSuggestedName}
                  sx={{ width: '275px' }}
                  variant={'text'}
                  size={'small'}>
                  use suggested name
                </Button>
              )
            }}
            placeholder={'Create a baseline name'}
            label={'Name'}
            value={baseline.baselineName ?? ''}
            onChange={({ target }) => handleChange({ baselineName: target.value })}
          />
        </Stack>
      )}
    </Box>
  );
}

BaselineDetails.propTypes = {
  baseline: PropTypes.object.isRequired,
  setBaseline: PropTypes.func.isRequired,
  highlightErrors: PropTypes.bool
};

BaselineDetails.defaultProps = {
  highlightErrors: false
};

export default BaselineDetails;
